import SelectedServiceListItem from "./SelectedServiceListItem";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { Button, Chip, Divider } from "@heroui/react";
import { Plus } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { formatCurrency } from "../../../../../shared/utils/utils";
import { i18n } from "@lingui/core";
import { useCalendarStore } from "@/pages/Dashboard/Calendar/store/useCalendarStore";
import { humanizeDuration } from "@/utils";
import { useClientFeature } from "@/hooks/useClientFeature";
import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { getDiscountCodeStatusLabelAndColor } from "@/utils/discount-code-utils";

const SelectedServiceList = ({
  selectedServices,
  setSelectedServices,
  services,
  isPreScheduled,
  isNewAppointment,
  addons = [],
  discountCode = null,
  setDiscountCode = () => {},
}) => {
  const { permissions } = useUserPermissionsStore();

  const { isFeatureEnabled } = useClientFeature();

  const {
    data: discountCodesData,
    isFetching: isFetchingDiscountCodes,
    processedErrorMessage: discountCodesErrorMessage,
  } = api.discountCode.useGetDiscountCodes(
    {},
    isFeatureEnabled("discountCodes") &&
      isNewAppointment &&
      !!permissions.view_discount_codes_page,
  );

  const { locations, selectedLocationId } = useCalendarStore((state) => ({
    locations: state.locations,
    selectedLocationId: state.selectedLocationId,
  }));

  const selectedLocation = locations.find(
    (location) => location.locationId === selectedLocationId,
  );

  const duration = (() => {
    if (selectedServices.length === 0) return 0;

    return selectedServices.reduce(
      (acc, service) =>
        acc +
        (service.timeOffDuration || 0) +
        (service.startDuration || 0) +
        (service.endDuration || 0),
      0,
    );
  })();

  const addonsDuration = addons.reduce(
    (acc, addon) => acc + (addon.durationMinsAdjustment || 0),
    0,
  );

  const totalDuration = duration + addonsDuration;

  const totalPrice = (() => {
    if (selectedServices.length === 0) return 0;

    const discountCodeData =
      discountCode && isNewAppointment
        ? discountCodesData?.discountCodes.find((dc) => dc.id == discountCode)
        : undefined;

    const discountedServicePrice = (service) => {
      const servicePrice = service.price || 0;

      if (!service.discountAmount) return servicePrice;

      if (service.discountType === "amount") {
        return servicePrice - service.discountAmount;
      }

      return servicePrice - (servicePrice * service.discountAmount) / 100;
    };

    const servicesPrice = selectedServices.reduce(
      (acc, service) => acc + discountedServicePrice(service),
      0,
    );

    const codeEligiblePrice = discountCodeData
      ? selectedServices
          .filter(
            (service) =>
              (discountCodeData.serviceIds.length === 0 ||
                discountCodeData.serviceIds.includes(service.serviceId)) &&
              (discountCodeData.userIds.length === 0 ||
                (service.users ?? []).some((userId) =>
                  discountCodeData.userIds.includes(Number(userId)),
                )),
          )
          .reduce((acc, service) => acc + discountedServicePrice(service), 0)
      : 0;

    const addonsPrice = addons.reduce(
      (acc, addon) => acc + (addon.pricePaidCents || 0) / 100,
      0,
    );

    const sumPrices = servicesPrice + addonsPrice;

    if (discountCodeData) {
      return (
        sumPrices - (codeEligiblePrice * discountCodeData.discountAmount) / 100
      );
    }

    return sumPrices;
  })();

  return (
    <div className="mb-5 flex flex-col gap-1 border-b border-gray-200 py-2">
      {selectedServices.length > 0 &&
        selectedServices.map((selectedService, index) => {
          const service = services.find(
            (service) => service.serviceId === selectedService.serviceId,
          );

          if (service == null) return null;

          const { startDuration, timeOffDuration, endDuration } =
            selectedService;

          const hasOnlyOneInput =
            timeOffDuration == null &&
            (startDuration == null || endDuration == null);

          return (
            <SelectedServiceListItem
              key={index}
              selectedService={selectedService}
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              service={service}
              index={index}
              isNewAppointment={isNewAppointment}
              isPreScheduled={isPreScheduled}
              startDuration={startDuration}
              hasOnlyOneInput={hasOnlyOneInput}
              timeOffDuration={timeOffDuration}
              endDuration={endDuration}
              defaultOpen={true}
              addons={addons}
            ></SelectedServiceListItem>
          );
        })}
      {!isPreScheduled && permissions.manage_appointments ? (
        <Button
          onPress={() => {
            const newServices = [
              ...selectedServices,
              {
                ...structuredClone(selectedServices.at(-1)),
                appointmentId: undefined,
                removed: false,
                defaultOpen: true,
              },
            ];

            setSelectedServices(newServices);
          }}
          variant="light"
          className="mb-2 rounded-full"
          size="sm"
          startContent={<Plus size={16} color={"#61906f"}></Plus>}
        >
          <Trans>Dodaj storitev</Trans>
        </Button>
      ) : null}

      {selectedServices?.length > 0 && (
        <>
          <div className="flex gap-2 text-sm">
            <p className="font-medium">
              <Trans>Skupni čas vseh storitev</Trans>:
            </p>
            <p>{humanizeDuration(totalDuration * 60000)}</p>
          </div>

          <div className="flex gap-2 text-sm">
            <p className="font-medium">
              <Trans>Skupna cena vseh storitev</Trans>:
            </p>
            <p>
              {formatCurrency({
                amount: totalPrice,
                currency: selectedLocation?.currency || "EUR",
                locale: i18n.locale,
              })}
            </p>
          </div>
        </>
      )}

      {isFeatureEnabled("discountCodes") &&
        isNewAppointment &&
        !!permissions.view_discount_codes_page && (
          <div className="flex flex-col gap-2">
            <Divider />

            <LimeSelect
              label={t`Koda za popust`}
              value={discountCode?.toString() ?? null}
              onSingleItemChange={(value) => {
                if (!value || value === "") {
                  setDiscountCode(null);
                } else {
                  setDiscountCode(Number(value));
                }
              }}
              disallowEmptySelection={false}
              items={
                discountCodesData?.discountCodes.map((dc) => {
                  const { label, color } = getDiscountCodeStatusLabelAndColor(
                    dc.status,
                  );

                  return {
                    label: `${dc.code} (${dc.discountAmount}%)`,
                    key: dc.id.toString(),
                    endContent: (
                      <Chip size="sm" color={color} variant="flat">
                        {label}
                      </Chip>
                    ),
                  };
                }) ?? []
              }
              isLoading={isFetchingDiscountCodes}
              errorMessage={discountCodesErrorMessage}
              size="sm"
            />
          </div>
        )}
    </div>
  );
};

export default SelectedServiceList;
